import { OPERATIONAL_MODULE_I18N_KEYS } from "@i18n/operationalModules";
import { OPERATIONAL_MODULE_SLUGS } from "@shared/api/server/gs3Server";
import {
  Archive,
  BookMarked,
  ChartColumn,
  ChartLine,
  Clipboard,
  ClipboardList,
  FileText,
  FolderOpen,
  Hammer,
  LayoutGrid,
  Map,
  TriangleAlert,
  UserRoundCheck,
  type LucideIcon,
} from "lucide-react";

// Apresentação dos módulos operacionais na sidebar.
//
// A chave do mapa é a coluna canônica `module` devolvida por
// GET /v1/user/me/modules (catálogo em
// docs/prd/operational-modules/operational-modules.md §4). O backend é a fonte
// da ordem (displayOrder) e da publicação (isReleased); aqui só mapeamos
// cada módulo para o rótulo i18n e o ícone do Design System.
//
// Ícones espelham a página de Sidebar do Design System no Figma
// (node 14020-45), com lucide-react no lugar dos Material Symbols. Exceção
// deliberada: General usa FileText (ficha técnica da estrutura — leitura dos dados
// cadastrados no Admin: identidade, KPIs, mapa, hidrologia, geometria, etc.), não
// House — reservado para a Home futura (panorama do tenant).
export interface OperationalModulePresentation {
  i18nKey: string;
  icon: LucideIcon;
  routePath?: string;
}

const OPERATIONAL_MODULE_ICONS: Record<string, LucideIcon> = {
  General: FileText,
  Monitoring: ChartColumn,
  Inspection: Clipboard,
  "Action plan": ClipboardList,
  "Stability analysis": Map,
  EoR: UserRoundCheck,
  ATO: Hammer,
  PSB: FolderOpen,
  GISTM: Archive,
  PAEBM: BookMarked,
  "Dam Break Study": ChartLine,
  "Risk Analysis": TriangleAlert,
};

// `routePath` = "/" + slug. O slug canônico (fonte única, também prefixo dos
// endpoints operacionais) vive na camada de API — OPERATIONAL_MODULE_SLUGS — e é
// reaproveitado aqui só para montar o path da sidebar. Fatia 3 cobre os 12 módulos.
export const OPERATIONAL_MODULE_PRESENTATION: Record<
  string,
  OperationalModulePresentation
> = Object.fromEntries(
  Object.entries(OPERATIONAL_MODULE_I18N_KEYS).map(([module, i18nKey]) => {
    const slug = OPERATIONAL_MODULE_SLUGS[module];
    return [
      module,
      {
        i18nKey,
        icon: OPERATIONAL_MODULE_ICONS[module] ?? LayoutGrid,
        routePath: slug ? `/${slug}` : undefined,
      },
    ];
  }),
);

// Ícone neutro para módulos futuros ainda sem mapeamento explícito (o catálogo
// pode crescer — ver operational-modules.md §4). Evita quebrar a sidebar caso o
// backend devolva um `module` que o front ainda não conhece.
export const FALLBACK_MODULE_ICON: LucideIcon = LayoutGrid;
